import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { DataTable } from "@/components/DataTable";
import { KnowledgeBaseV2WithTags } from "@/generated";
import { BookOpen, CheckCheck, Plus, SearchX } from "lucide-react";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { DATA_KEYS, prefetchQuery, useRemoteData } from "@/utils/useRemoteData";
import { FlowTagsStack } from "../Tags/FlowTagsStack";
import { FlowTagActionsDialog, TagAction } from "../Tags/FlowTagActionsDialog";
import { FlowTagsMenu } from "../Tags/FlowTagsMenu";
import { useSpotUser } from "@/utils/spotUser";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { OptionType } from "../Form/SelectField";

import { SpotToast } from "../SpotToast";
import { SearchBar } from "../SearchBar";
import WorkshopEmpty from "../Views/Workshop/WorkshopEmpty";
import { timeAgo } from "@/utils/generic";
import { useKBsTableContextMenu } from "./useKBsTableContextMenu";
import { KBDeleteDialog } from "../Modals/KBDeleteDialog";
import { KBDetailsDialog } from "../Dialogs/KBDetailsDialog";
import LoadingPageWorkshop from "../Views/Workshop/LoadingPageWorkshop";
import { KBIconPicker } from "./KBIconPicker";
import { IngestedBadge } from "./IngestedBadge";

const SizeOptions = [
  { label: "15 per page", value: "15" },
  { label: "20 per page", value: "20" },
  { label: "25 per page", value: "25" },
  { label: "30 per page", value: "30" },
];

const StatusOptions = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Deleted",
    value: "deleted",
  },
];

export const KnowledgeBasesTable = ({
  defaultPageSize,
  ignoreIds,
  readOnly,
}: {
  defaultPageSize?: number;
  ignoreIds?: string[];
  readOnly?: boolean;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagAction, setTagAction] = useState<TagAction>();

  const [selectedStatus, setSelectedStatus] = useState(StatusOptions[0]);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] =
    useState<KnowledgeBaseV2WithTags | null>(null);

  const [pageSize, setPageSize] = useState(() => {
    if (defaultPageSize) {
      return defaultPageSize;
    }
    const height = window.innerHeight;
    if (height < 1050) {
      return Number(SizeOptions[0].value); // 15
    } else if (height < 1220) {
      return Number(SizeOptions[1].value); // 30
    } else {
      return Number(SizeOptions[2].value); // 45
    }
  }); // Default value

  /* Tags data */
  const { data: tagsData, isLoading: isTagsLoading } = useRemoteData(
    DATA_KEYS.TAGS,
    {
      path: { group_id: spotGroup.id },
    }
  );

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  /* Knowledge Bases data */
  const { data: knowledgeBasesData, isLoading } = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2,
    {
      path: { group_id: spotGroup.id },
      query: {
        page: currentPage,
        size: pageSize,
        query: searchValue || undefined,
        tags: selectedTags?.join(",") || undefined,
        order_by: sortBy || undefined,
        sort: sortOrder || undefined,
        ignore_ids: ignoreIds?.join(",") || undefined,
      },
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const onSort = (sorting: SortingState) => {
    if (sorting.length > 0) {
      setSortBy(sorting[0].id);
      setSortOrder(sorting[0].desc ? "desc" : "asc");
    } else {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  /* Memo */
  const getNoDataMessage = useMemo(() => {
    return searchValue !== "" && searchValue != undefined
      ? "The search retrieved no results."
      : selectedStatus.value === "deleted"
        ? "There are no deleted knowledge bases."
        : "There are no knowledge bases to display.";
  }, [searchValue, selectedStatus.value]);

  const { getContextMenuItems } = useKBsTableContextMenu({
    setDeleteModalOpen,
    setDetailsModalOpen,
    knowledgeBase: selectedKnowledgeBase,
  });

  /* Columns */
  const columns: ColumnDef<KnowledgeBaseV2WithTags, any>[] = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex gap-2 items-center">
            <div
              className="flex h-fit"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <KBIconPicker
                icon={(row.original.meta?.icon as string) || "kb"}
                kbId={row.original.id}
                size="sm"
              />
            </div>
            <p className="font-base text-sm text-primary">
              {row.original.name}
            </p>
          </div>
        ),
        meta: {
          isSortable: true,
          width: "30%",
        },
      },
      {
        id: "tags",
        cell: ({ row }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <FlowTagsStack
              onEditTag={(tag) => setTagAction({ type: "edit", tag })}
              onDeleteTag={(tag) => setTagAction({ type: "delete", tag })}
              entityId={row.original.id}
              entityType="knowledge_base"
              entityTags={row.original.tags || []}
              disabled={readOnly || selectedStatus.value === "deleted"}
            />
          </div>
        ),
        header: "Tags",
        meta: {
          isSortable: false,
          width: "15%",
          noEllipsis: true,
        },
      },
      {
        id: "documents",
        header: "Documents",
        cell: ({ row }) => {
          const total = row.original.num_docs ?? 0;
          if (total === 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <span className="text-sm tabular-nums">
              {total.toLocaleString()}
            </span>
          );
        },
        meta: {
          width: "10%",
        },
      },
      {
        id: "ingested",
        header: "Ingested",
        cell: ({ row }) => {
          const total = row.original.num_docs ?? 0;
          if (total === 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <IngestedBadge
              ingested={row.original.trained_documents ?? 0}
              total={total}
            />
          );
        },
        meta: {
          width: "10%",
        },
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        cell: ({ row }) => {
          return row.original.updated_at
            ? timeAgo(row.original.updated_at)
            : "-";
        },
        header: "Last modified",
        sortDescFirst: true,
        meta: {
          isSortable: true,
          width: "15%",
        },
      },
    ],
    [selectedStatus.value, readOnly]
  );

  const handleRowClick = (knowledgeBase: KnowledgeBaseV2WithTags) => {
    if (selectedStatus.value === "deleted") {
      SpotToast.info(
        "This knowledge base was deleted. To use it, you must restore it first."
      );
    }
  };

  const getRowHref = (knowledgeBase: KnowledgeBaseV2WithTags) => {
    if (selectedStatus.value === "deleted") {
      return "";
    }
    return `/data-hub/${knowledgeBase.id}`;
  };

  const handlePageSizeChange = (value: OptionType) => {
    setPageSize(Number(value.value));
  };

  const handleContextMenuOpen = (knowledgeBase: KnowledgeBaseV2WithTags) => {
    setSelectedKnowledgeBase(knowledgeBase);
  };

  const isLoadingPage = isLoading || isTagsLoading;
  const hasSearch = searchValue !== "";
  const hasOtherFilters =
    selectedStatus.value !== "all" || selectedTags.length > 0;
  const hasActiveFilters = hasSearch || hasOtherFilters;

  const hasNone =
    (knowledgeBasesData?.total === 0 || !knowledgeBasesData) &&
    !hasActiveFilters;
  const allIgnored = hasNone && Boolean(ignoreIds?.length);
  const isEmpty = hasNone && !allIgnored;

  const clearFilters = () => {
    setSearchValue("");
    setSelectedStatus(StatusOptions[0]);
    setSelectedTags([]);
    setCurrentPage(1);
  };

  const noMatchDescription = hasSearch
    ? hasOtherFilters
      ? "Try a different search term or adjust your filters."
      : "Try a different search term."
    : "Try adjusting your filters.";

  return (
    <>
      <div className="w-full mb-4">
        <div className="flex justify-between gap-2 items-center">
          <SearchBar
            placeholder="Search in this workspace"
            isDebounced
            value={searchValue}
            setValue={handleSearchValueChange}
          />
          <div className="flex gap-2 items-center">
            <FlowTagsMenu
              tags={tagsData || []}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
            />
          </div>
        </div>
      </div>
      {isLoadingPage && (
        <LoadingPageWorkshop pageSize={pageSize} columns={columns} />
      )}

      {!isLoadingPage && (
        <DataTable<KnowledgeBaseV2WithTags>
          columns={columns}
          data={knowledgeBasesData?.items || []}
          isLoading={isLoading}
          loadingRows={pageSize}
          rowOnClick={handleRowClick}
          rowHref={getRowHref}
          noDataMessage={getNoDataMessage}
          onSort={onSort}
          contextMenuItems={readOnly ? undefined : getContextMenuItems}
          onContextMenuOpen={readOnly ? undefined : handleContextMenuOpen}
          noPrefetchLinks
          tableControls={{
            data: knowledgeBasesData,
            currentPage,
            setCurrentPage,
            pageSize,
            onPageSizeChange: handlePageSizeChange,
          }}
          emptyState={
            hasActiveFilters ? (
              <NoxusEmptyBlock
                size="sm"
                icon={SearchX}
                title={
                  hasSearch
                    ? "No knowledge bases match your search"
                    : "No knowledge bases match your filters"
                }
                description={noMatchDescription}
                button={
                  hasSearch && !hasOtherFilters
                    ? "Clear search"
                    : "Clear filters"
                }
                buttonVariant="outline"
                onClick={clearFilters}
              />
            ) : allIgnored ? (
              <NoxusEmptyBlock
                size="sm"
                icon={CheckCheck}
                title="All Knowledge Bases are already open"
                description="Every Knowledge Base in this workspace is already open in a tab."
              />
            ) : isEmpty ? (
              <WorkshopEmpty
                toolId="knowledge-base"
                icon={BookOpen}
                title="No Knowledge Bases created yet"
                readerDescription="There are currently no Knowledge Bases available for this group."
                button="Create Knowledge Base"
                buttonIcon={Plus}
                size="sm"
                description="Centralize varied data into structured repositories, enabling agentic RAG access via agents and flows."
              />
            ) : undefined
          }
        />
      )}
      {selectedKnowledgeBase && detailsModalOpen && (
        <KBDetailsDialog
          isOpen={detailsModalOpen}
          onOpenChange={setDetailsModalOpen}
          kb={selectedKnowledgeBase}
        />
      )}
      {selectedKnowledgeBase && deleteModalOpen && (
        <KBDeleteDialog
          isOpen={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          kbId={selectedKnowledgeBase.id}
          kbName={selectedKnowledgeBase.name}
          kbIcon={selectedKnowledgeBase.meta?.icon as string | undefined}
        />
      )}
      <FlowTagActionsDialog
        action={tagAction}
        setAction={setTagAction}
        onDeleted={(tagId) => {
          setSelectedTags((current) => current.filter((id) => id !== tagId));
        }}
      />
    </>
  );
};
